import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Authored HTML at full fidelity — the conversation of a ticket, its description, a KB article —
// as it was written (typically in HaloPSA or an email client): tables, images, inline styles,
// fonts, colours. The strict `sanitizeHtml` of RichTextView keeps only a handful of tags and no
// attributes, which turns such content into an unformatted wall; this renders it instead in a
// SANDBOXED iframe:
//   * no `allow-scripts`: nothing in the document can run, whatever survives sanitising;
//   * `allow-same-origin` only so the frame can be measured and sized to its content;
//   * links open in a new tab (`<base target=_blank>`, `allow-popups-to-escape-sandbox`);
//   * a light "paper" surface whatever the app theme — content authored on white stays readable
//     in dark mode (its inline colours assume white);
//   * images and tables are clamped to the frame width; wide tables scroll inside it;
//   * a CSP in the document allows images, fonts and inline styles only (no scripts, connections,
//     forms or frames), and images load without a referrer.
// (Inbound email bodies use the stricter, fixed-height viewer in modules/email/MessageView.)
// Sanitising still removes active content (scripts, frames, forms, event handlers, javascript:
// URLs) as defence in depth.

const DROP = new Set(['SCRIPT', 'IFRAME', 'FRAME', 'FRAMESET', 'OBJECT', 'EMBED', 'APPLET', 'FORM', 'INPUT', 'BUTTON', 'TEXTAREA', 'SELECT', 'LINK', 'META', 'BASE', 'NOSCRIPT', 'TEMPLATE', 'SVG', 'MATH']);
const URL_ATTRS = new Set(['href', 'src', 'action', 'formaction', 'xlink:href', 'background', 'poster', 'srcset']);
const SAFE_URL = /^(https?:|mailto:|tel:|cid:|#|\/)/i;
const SAFE_IMAGE = /^data:image\/(png|gif|jpe?g|webp);/i;

/** Remove active content from an authored HTML document, keeping its formatting. */
export function sanitizeDocument(html: string): string {
  if (typeof DOMParser === 'undefined') return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const walk = (node: Element) => {
    for (const child of Array.from(node.children)) {
      if (DROP.has(child.tagName)) {
        child.remove();
        continue;
      }
      for (const attr of Array.from(child.attributes)) {
        const name = attr.name.toLowerCase();
        const value = attr.value.trim();
        if (name.startsWith('on') || name === 'srcdoc' || name === 'formaction') child.removeAttribute(attr.name);
        else if (URL_ATTRS.has(name)) {
          const ok = SAFE_URL.test(value) || (name === 'src' && child.tagName === 'IMG' && SAFE_IMAGE.test(value));
          if (!ok || name === 'srcset') child.removeAttribute(attr.name);
        } else if (name === 'style' && /expression\s*\(|javascript:|url\s*\(\s*['"]?\s*javascript:/i.test(value)) child.removeAttribute(attr.name);
      }
      if (child.tagName === 'A') child.setAttribute('rel', 'noopener noreferrer');
      walk(child);
    }
  };
  // <style> blocks stay: inside the sandboxed frame they only style the document itself.
  walk(doc.head);
  walk(doc.body);
  const styles = Array.from(doc.head.querySelectorAll('style')).map((s) => s.outerHTML).join('');
  return styles + doc.body.innerHTML;
}

/** Whether HTML carries formatting the strict inline renderer would destroy. */
export function needsDocumentRendering(html: string): boolean {
  return /<(table|img|font|style|center|span[^>]*\sstyle=|div[^>]*\sstyle=|p[^>]*\sstyle=|td|h5|h6)\b/i.test(html);
}

// The frame's own stylesheet. It cannot read the app's design tokens (it is a separate
// document), so the paper surface is spelled out here once.
const FRAME_CSS = [
  ':root{color-scheme:light}',
  'html,body{margin:0;background:#fff;color:#1a1a1a}',
  'body{padding:12px 14px;font:14px/1.55 Inter,system-ui,-apple-system,"Segoe UI",sans-serif;overflow-wrap:anywhere}',
  'img{max-width:100%;height:auto}',
  'table{max-width:100%;border-collapse:collapse;display:block;overflow-x:auto}',
  'pre{white-space:pre-wrap}',
  'a{color:#9a3412}',
  'p:first-child{margin-top:0}p:last-child{margin-bottom:0}',
].join('');

// Belt and braces with the sandbox: nothing may execute or be fetched except images and fonts
// (and inline styles); no forms, no frames, no connections.
const FRAME_CSP = "default-src 'none'; img-src https: data: cid:; style-src 'unsafe-inline'; font-src https: data:; form-action 'none'; frame-src 'none'";

export function documentSrc(html: string): string {
  return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${FRAME_CSP}"><base target="_blank"><style>${FRAME_CSS}</style></head><body>${sanitizeDocument(html)}</body></html>`;
}

export function HtmlDocument({ html, title = 'Message', maxHeight }: { html: string; title?: string; maxHeight?: number }) {
  const ref = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(48);
  const srcDoc = useMemo(() => documentSrc(html), [html]);

  const measure = useCallback(() => {
    const body = ref.current?.contentDocument?.body;
    if (!body) return;
    const h = Math.ceil(Math.max(body.scrollHeight, body.offsetHeight)) + 2;
    setHeight(maxHeight ? Math.min(h, maxHeight) : h);
  }, [maxHeight]);

  useEffect(() => {
    const frame = ref.current;
    if (!frame) return;
    let observer: ResizeObserver | undefined;
    const onLoad = () => {
      measure();
      const body = frame.contentDocument?.body;
      if (body && typeof ResizeObserver !== 'undefined') {
        observer = new ResizeObserver(measure);
        observer.observe(body);
      }
      // Images load after the document; re-measure as each arrives.
      frame.contentDocument?.querySelectorAll('img').forEach((img) => img.addEventListener('load', measure));
    };
    frame.addEventListener('load', onLoad);
    return () => {
      frame.removeEventListener('load', onLoad);
      observer?.disconnect();
    };
  }, [srcDoc, measure]);

  return (
    <iframe
      ref={ref}
      className="hf-html-document"
      title={title}
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      referrerPolicy="no-referrer"
      srcDoc={srcDoc}
      style={{ height }}
      scrolling={maxHeight ? 'auto' : 'no'}
    />
  );
}
